import { conceptUrgency, type GraphScores, type MasteryState } from "../../../src/index.js";
import { getRoundIndex } from "./rotation.js";

const DEFAULT_TARGET = 1400;
const EPS = 1e-9;

function roundUrgency(
  scores: GraphScores,
  state: MasteryState,
  conceptIds: readonly string[],
  targetElo: number
): number {
  if (conceptIds.length === 0) return 0;
  let maxU = 0;
  for (const id of new Set(conceptIds)) {
    const u = conceptUrgency(scores, state, id, targetElo);
    if (u > maxU) maxU = u;
  }
  return maxU;
}

export type PickRoundByMasteryArgs = {
  gameType: string;
  deckLength: number;
  mastery: MasteryState;
  scores: GraphScores;
  /** Concept ids touched by the round at `roundIndex` (duplicates ignored). */
  getConceptIds: (roundIndex: number) => readonly string[];
  targetElo?: number;
};

/**
 * Picks a round index that maximizes practice need (max per-concept `conceptUrgency` in the round).
 * Ties break by smallest forward distance from the rotation cursor so `advanceRound` still adds variety.
 */
export function pickRoundIndexByMastery(args: PickRoundByMasteryArgs): number {
  const { gameType, deckLength, mastery, scores, getConceptIds, targetElo = DEFAULT_TARGET } = args;
  if (deckLength <= 0) return 0;

  const urgencies: number[] = [];
  for (let i = 0; i < deckLength; i++) {
    urgencies.push(roundUrgency(scores, mastery, getConceptIds(i), targetElo));
  }
  const best = Math.max(...urgencies);
  const candidates: number[] = [];
  for (let i = 0; i < deckLength; i++) {
    if (Math.abs(urgencies[i]! - best) < EPS) candidates.push(i);
  }

  const cursor = getRoundIndex(gameType, deckLength);
  let pick = candidates[0]!;
  let bestDist = (pick - cursor + deckLength) % deckLength;
  for (const i of candidates) {
    const d = (i - cursor + deckLength) % deckLength;
    if (d < bestDist || (d === bestDist && i < pick)) {
      pick = i;
      bestDist = d;
    }
  }
  return pick;
}
